import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { PublicLayout } from "../components/public/PublicLayout";
import { MapPin, Globe, Store } from "lucide-react";

const COUNTRIES = ["Portugal", "Ireland", "Spain", "France", "United Kingdom", "Germany"];

export default function WhereToBuy() {
  const { t } = useLang();
  const [country, setCountry] = useState("");
  useEffect(() => { document.title = "Where to Buy | Sambiva"; }, []);

  const { data: locations = [], isLoading } = useQuery({
    queryKey: ["locations", country],
    queryFn: async () => (await api.get(`/retail-locations${country ? `?country=${encodeURIComponent(country)}` : ""}`)).data,
  });

  return (
    <PublicLayout>
      <section className="bg-sambiva-green text-sambiva-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 text-center">
          <MapPin className="mx-auto mb-3 text-sambiva-yellow" size={36} />
          <h1 className="font-serif text-4xl sm:text-5xl mb-3">Find Sambiva near you.</h1>
          <p className="text-sambiva-cream/85 text-lg max-w-2xl mx-auto">Discover the shops and stores stocking Sambiva across Europe.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          <button data-testid="country-all" onClick={() => setCountry("")} className={`text-sm px-4 py-2 rounded-full font-medium ${!country ? "bg-sambiva-red text-white" : "bg-white text-sambiva-charcoal hover:bg-sambiva-red/10"}`}>All Countries</button>
          {COUNTRIES.map((c) => (
            <button key={c} data-testid={`country-${c.replace(/\s+/g, "-")}`} onClick={() => setCountry(c)} className={`text-sm px-4 py-2 rounded-full font-medium ${country === c ? "bg-sambiva-red text-white" : "bg-white text-sambiva-charcoal hover:bg-sambiva-red/10"}`}>{c}</button>
          ))}
        </div>

        {isLoading ? (
          <div className="text-center py-16 text-sambiva-muted">{t("common.loading")}</div>
        ) : locations.length === 0 ? (
          <div className="text-center py-16">
            <Store className="mx-auto text-sambiva-muted mb-3" size={40} />
            <p className="text-sambiva-muted">No stores listed here yet — more coming soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="locations-grid">
            {locations.map((loc) => (
              <div key={loc.id} data-testid={`location-${loc.id}`} className="bg-white rounded-2xl p-6 border border-sambiva-charcoal/5 card-hover">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-11 h-11 rounded-xl bg-sambiva-red/10 flex items-center justify-center">
                    {loc.type === "Online Store" ? <Globe className="text-sambiva-red" size={20} /> : <Store className="text-sambiva-red" size={20} />}
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-sambiva-green/10 text-sambiva-green">{loc.type}</span>
                </div>
                <h3 className="font-serif text-xl text-sambiva-charcoal mb-1">{loc.name}</h3>
                <p className="text-sm text-sambiva-muted mb-1">{loc.address}</p>
                <p className="text-sm text-sambiva-muted mb-4">{loc.city}, {loc.country}</p>
                <a href={loc.website} target="_blank" rel="noreferrer" className="text-sm font-semibold text-sambiva-red hover:underline">Visit website →</a>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 rounded-3xl bg-white border border-dashed border-sambiva-charcoal/15 h-56 flex items-center justify-center text-sambiva-muted">
          <div className="text-center">
            <MapPin className="mx-auto mb-2" size={28} />
            <p className="text-sm">Interactive store map coming soon.</p>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
